import { useCallback, useEffect, useState } from 'react';
import { Loader2, Calendar as CalendarIcon, Plus, Trash2, X, ChevronDown, ChevronUp, Users, Globe, BarChart3, Check, Megaphone } from 'lucide-react';
import { TimeDropdown } from '@/components/TimeDropdown';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { extractErrorMessage } from '@/lib/errorMessage';
import { DateInput } from '@/components/ui/DateInput';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SuperAdminDemoAvailability, type DemoCfg } from '@/components/super-admin/SuperAdminDemoAvailability';
import { useSidebarEdge } from '@/lib/useSidebarEdge';
import { formatDate } from '@/lib/dateFormat';

// ────────────────────────────────────────────────────────────────────────────
// Demo/Lead Assign — a clone of the tenant Lead Assign page. Each demo-team
// super-admin has an Active toggle, a "Leads X/Y" (daily) + "Demos X/Y" cap,
// and country-wise routing. The website round-robin honors all of these.
// ────────────────────────────────────────────────────────────────────────────

interface Assignment {
  id: string;
  name: string;
  email: string;
  hasAvailability: boolean;
  availabilityActive: boolean;
  assignActive: boolean;
  demoLimit: number;
  dailyLeadLimit: number;
  monthlyLeadLimit: number;
  includeCountryIds: string[];
  excludeCountryIds: string[];
  includeCampaigns: string[];
  excludeCampaigns: string[];
  daysOff: string[]; // yyyy-MM-dd
  assignWorkStart: string;
  assignWorkEnd: string;
  upcoming: number;
  leadsToday: number;
}
interface Country { id: string; name: string; flagEmoji?: string | null }

// Today (local) as yyyy-MM-dd — past days off are hidden.
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const upcomingDaysOff = (r: Assignment) => (r.daysOff ?? []).filter(d => d >= todayStr()).sort();

const initials = (name: string) => name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

export function SuperAdminDemoAssign() {
  const [rows, setRows] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [campaigns, setCampaigns] = useState<string[]>([]);
  const [daysOffOpenId, setDaysOffOpenId] = useState<string | null>(null);
  const [newDayOff, setNewDayOff] = useState('');
  const [savingDaysOffId, setSavingDaysOffId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Assignment | null>(null);
  const [availabilityFor, setAvailabilityFor] = useState<Assignment | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await saApi.get('/super-admin/demo/assignments');
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to load assignments'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    saApi.get('/geo/countries').then(({ data }) => setCountries(Array.isArray(data) ? data : [])).catch(() => {});
    saApi.get('/super-admin/demo/assignments/campaigns').then(({ data }) => setCampaigns(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const patchActive = async (r: Assignment) => {
    setRows(rs => rs.map(x => x.id === r.id ? { ...x, assignActive: !x.assignActive } : x));
    try { await saApi.patch(`/super-admin/demo/assignments/${r.id}`, { assignActive: !r.assignActive }); }
    catch (e) { toast('error', extractErrorMessage(e, 'Failed to update')); load(); }
  };

  const removeMember = async (r: Assignment) => {
    setRows(rs => rs.filter(x => x.id !== r.id));
    try { await saApi.delete(`/super-admin/demo/assignments/${r.id}`); toast('success', `${r.name} removed`); }
    catch (e) { toast('error', extractErrorMessage(e, 'Failed to remove')); load(); }
  };

  // Days off save immediately; past dates are pruned on each change.
  const saveDaysOff = async (r: Assignment, next: string[]) => {
    setSavingDaysOffId(r.id);
    try {
      await saApi.patch(`/super-admin/demo/assignments/${r.id}`, { daysOff: next });
      setRows(rs => rs.map(x => x.id === r.id ? { ...x, daysOff: next } : x));
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to update days off'));
    } finally {
      setSavingDaysOffId(null);
    }
  };
  const addDayOff = async (r: Assignment) => {
    if (!newDayOff) return;
    if (newDayOff < todayStr()) { toast('error', 'Pick today or a future date'); return; }
    await saveDaysOff(r, [...new Set([...upcomingDaysOff(r), newDayOff])].sort());
    setNewDayOff('');
  };

  const saveSettings = async (id: string, body: Partial<Assignment>) => {
    try {
      await saApi.patch(`/super-admin/demo/assignments/${id}`, body);
      setRows(rs => rs.map(x => x.id === id ? { ...x, ...body } : x));
      toast('success', 'Saved');
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to save'));
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Demo &amp; Lead Assignment</h2>
          <p className="text-sm text-slate-500">Country-wise round-robin + daily lead and demo caps.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold flex-shrink-0">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center">
          <Users className="h-8 w-8 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No one on the demo team yet — use “Add”.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(r => {
            const isOpen = expanded === r.id;
            const leadPct = r.dailyLeadLimit > 0 ? Math.min(100, Math.round((r.leadsToday / r.dailyLeadLimit) * 100)) : 0;
            const demoPct = r.demoLimit > 0 ? Math.min(100, Math.round((r.upcoming / r.demoLimit) * 100)) : 0;
            return (
              <div key={r.id} className="bg-white rounded-xl border border-slate-200">
                <div className="p-4 flex items-center gap-4">
                  <div className="h-11 w-11 rounded-full bg-indigo-100 text-[#6366F1] flex items-center justify-center font-bold flex-shrink-0">{initials(r.name)}</div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-slate-900 truncate">{r.name}</p>
                      {r.availabilityActive ? (
                        <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Active</span>
                      ) : (
                        <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">{r.hasAvailability ? 'Not accepting' : 'No availability'}</span>
                      )}
                      {!r.assignActive && (
                        <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">Paused</span>
                      )}
                      {upcomingDaysOff(r).slice(0, 3).map(d => (
                        <span key={d} className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-full">Off {formatDate(d)}</span>
                      ))}
                      {upcomingDaysOff(r).length > 3 && <span className="text-[10px] text-slate-400">+{upcomingDaysOff(r).length - 3}</span>}
                      <button
                        type="button"
                        onClick={() => { setDaysOffOpenId(prev => prev === r.id ? null : r.id); setNewDayOff(''); }}
                        className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 border border-dashed border-slate-300 hover:border-[#6366F1] hover:text-[#6366F1] px-1.5 py-0.5 rounded-full"
                      >
                        <Plus className="h-2.5 w-2.5" /> Days off
                      </button>
                      {r.includeCountryIds.length > 0 && (
                        <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" title="Handles only these countries"><Globe className="h-2.5 w-2.5" />{r.includeCountryIds.length}</span>
                      )}
                      {r.excludeCountryIds.length > 0 && (
                        <span className="text-[10px] font-semibold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" title="Excludes these countries"><Globe className="h-2.5 w-2.5" />−{r.excludeCountryIds.length}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{r.email}</p>
                  </div>

                  <Meter icon={<Users className="h-3 w-3" />} label="Leads" value={`${r.leadsToday}/${r.dailyLeadLimit}`} pct={leadPct} />
                  <Meter icon={<CalendarIcon className="h-3 w-3" />} label="Demos" value={`${r.upcoming}/${r.demoLimit}`} pct={demoPct} />

                  {/* Remove sits apart from the expand arrow (toggle in between) so
                      it can't be hit by mistake; it also asks for confirmation. */}
                  <button onClick={() => setConfirmRemove(r)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex-shrink-0 mr-2" title="Remove from demo team">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => patchActive(r)}
                    className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${r.assignActive ? 'bg-[#6366F1]' : 'bg-slate-200'}`}
                    title={r.assignActive ? 'In round-robin' : 'Excluded'}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${r.assignActive ? 'left-[22px]' : 'left-0.5'}`} />
                  </button>
                  <button
                    onClick={() => setAvailabilityFor(r)}
                    className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 flex-shrink-0"
                    title="View / edit time availability"
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </button>
                  <button onClick={() => setExpanded(isOpen ? null : r.id)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 flex-shrink-0" title="Settings">
                    {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>

                {/* Days off editor — no leads/demos routed on these dates */}
                {daysOffOpenId === r.id && (
                  <div className="border-t border-slate-100 bg-amber-50/30 px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-600">Days off:</span>
                      {upcomingDaysOff(r).length === 0 && <span className="text-xs text-slate-400">None</span>}
                      {upcomingDaysOff(r).map(d => (
                        <span key={d} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-100">
                          {formatDate(d)}
                          <button
                            type="button"
                            onClick={() => saveDaysOff(r, upcomingDaysOff(r).filter(x => x !== d))}
                            disabled={savingDaysOffId === r.id}
                            className="leading-none hover:opacity-70 disabled:opacity-40"
                            aria-label={`Remove ${formatDate(d)}`}
                          >×</button>
                        </span>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <div className="w-44"><DateInput value={newDayOff} onChange={setNewDayOff} /></div>
                      <button
                        type="button"
                        onClick={() => addDayOff(r)}
                        disabled={!newDayOff || savingDaysOffId === r.id}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-xs font-medium disabled:opacity-50"
                      >
                        {savingDaysOffId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        Add day off
                      </button>
                      <span className="text-[11px] text-slate-400">No new leads or demos are routed to this admin on these dates.</span>
                    </div>
                  </div>
                )}

                {isOpen && <SettingsPanel row={r} countries={countries} campaigns={campaigns} onSave={b => saveSettings(r.id, b)} />}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <AddMemberModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
      {availabilityFor && (
        <AvailabilityDrawer
          admin={availabilityFor}
          onClose={() => { setAvailabilityFor(null); load(); }}
        />
      )}
      <ConfirmModal
        isOpen={!!confirmRemove}
        title="Remove from demo team?"
        message={`${confirmRemove?.name ?? 'This admin'} will stop receiving round-robin leads and demos. Existing prospects and bookings are not changed.`}
        confirmText="Remove"
        onConfirm={() => { const r = confirmRemove; setConfirmRemove(null); if (r) removeMember(r); }}
        onCancel={() => setConfirmRemove(null)}
      />
    </div>
  );
}

// Another admin's demo availability (vendor Time-Availability parity), in a
// drawer that spans up to the sidebar.
function AvailabilityDrawer({ admin, onClose }: { admin: Assignment; onClose: () => void }) {
  const left = useSidebarEdge();
  const apiBase = `/super-admin/demo/admins/${admin.id}`;
  const [cfg, setCfg] = useState<DemoCfg | null>(null);

  useEffect(() => {
    saApi.get(`${apiBase}/config`)
      .then(({ data }) => setCfg(data))
      .catch((e) => { toast('error', extractErrorMessage(e, 'Failed to load availability')); onClose(); });
  }, [apiBase]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-y-0 right-0 z-50 flex flex-col bg-[#F8F9FA] shadow-2xl border-l border-slate-200 animate-in slide-in-from-right duration-300"
      style={{ left }}
    >
      <div className="flex h-12 sm:h-14 flex-shrink-0 items-center justify-between gap-3 border-b border-slate-100 bg-white px-3 sm:px-6">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{admin.name} — Time availability</p>
          <p className="truncate text-xs text-slate-500">{admin.email}</p>
        </div>
        <button onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        {!cfg ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" /></div>
        ) : (
          <SuperAdminDemoAvailability cfg={cfg} apiBase={apiBase} onSaved={setCfg} />
        )}
      </div>
    </div>
  );
}

function Meter({ icon, label, value, pct }: { icon: React.ReactNode; label: string; value: string; pct: number }) {
  const full = pct >= 100;
  return (
    <div className="w-32 flex-shrink-0 hidden md:block">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="inline-flex items-center gap-1 text-slate-500">{icon} {label}</span>
        <span className={`font-semibold ${full ? 'text-amber-600' : 'text-slate-700'}`}>{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${full ? 'bg-amber-400' : 'bg-[#6366F1]'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SettingsPanel({ row, countries, campaigns, onSave }: { row: Assignment; countries: Country[]; campaigns: string[]; onSave: (b: Partial<Assignment>) => void }) {
  const [daily, setDaily] = useState(row.dailyLeadLimit);
  const [monthly, setMonthly] = useState(row.monthlyLeadLimit);
  const [demo, setDemo] = useState(row.demoLimit);
  const [inc, setInc] = useState<string[]>(row.includeCountryIds);
  const [exc, setExc] = useState<string[]>(row.excludeCountryIds);
  const [incC, setIncC] = useState<string[]>(row.includeCampaigns ?? []);
  const [excC, setExcC] = useState<string[]>(row.excludeCampaigns ?? []);
  const [workStart, setWorkStart] = useState(row.assignWorkStart || '');
  const [workEnd, setWorkEnd] = useState(row.assignWorkEnd || '');
  const [saving, setSaving] = useState(false);

  const toggle = (arr: string[], set: (v: string[]) => void, id: string) => set(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);

  const save = async () => {
    setSaving(true);
    await onSave({ dailyLeadLimit: daily, monthlyLeadLimit: monthly, demoLimit: demo, includeCountryIds: inc, excludeCountryIds: exc, includeCampaigns: incC, excludeCampaigns: excC, assignWorkStart: workStart, assignWorkEnd: workEnd } as any);
    setSaving(false);
  };

  const IN = 'h-9 px-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]';

  return (
    <div className="border-t border-slate-100 p-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* Column 1: Lead Limit + Demo Limit stacked */}
        <div className="space-y-4">
        <div className="border border-slate-100 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0"><BarChart3 className="h-4 w-4 text-purple-600" /></div>
            <div><p className="text-sm font-semibold text-slate-800">Lead Limit</p><p className="text-xs text-slate-400">Daily capacity per representative</p></div>
          </div>
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Daily Capacity</label>
            <div className="flex items-center gap-2"><input type="number" min={0} value={daily} onChange={e => setDaily(Math.max(0, +e.target.value))} className={`w-24 ${IN}`} /><span className="text-xs text-slate-500">Leads</span></div>
          </div>
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Monthly Cap</label>
            <div className="flex items-center gap-2"><input type="number" min={0} value={monthly} onChange={e => setMonthly(Math.max(0, +e.target.value))} className={`w-24 ${IN}`} /><span className="text-xs text-slate-500">Leads / month</span></div>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2"><p className="text-xs text-blue-600">Note: This is the maximum number of leads that can be assigned per day.</p></div>
        </div>

        {/* Demo Limit */}
        <div className="border border-slate-100 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0"><BarChart3 className="h-4 w-4 text-purple-600" /></div>
            <div><p className="text-sm font-semibold text-slate-800">Demo Limit</p><p className="text-xs text-slate-400">Weekly demonstration quota</p></div>
          </div>
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Weekly Quota</label>
            <div className="flex items-center gap-2"><input type="number" min={0} value={demo} onChange={e => setDemo(Math.max(0, +e.target.value))} className={`w-24 ${IN}`} /><span className="text-xs text-slate-500">Demos</span></div>
          </div>
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Working Hours</label>
            <div className="grid grid-cols-2 gap-2">
              <TimeDropdown value={workStart} onChange={setWorkStart} />
              <TimeDropdown value={workEnd} onChange={setWorkEnd} />
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Leads only routed within these hours (empty = all day).</p>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2"><p className="text-xs text-blue-600">Note: Maximum number of demos that can be scheduled per week.</p></div>
        </div>

        </div>

        {/* Column 2: Regional Routing */}
        <div className="border border-slate-100 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0"><Globe className="h-4 w-4 text-emerald-600" /></div>
            <div><p className="text-sm font-semibold text-emerald-700">Regional Routing</p><p className="text-xs text-slate-400">Country-based lead assignment</p></div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1.5"><Check className="h-3.5 w-3.5 text-emerald-500" /><span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Included Countries</span>{inc.length > 0 && <span className="text-xs text-slate-400">({inc.length})</span>}</div>
            <CountryList selected={inc} mode="include" countries={countries} onToggle={id => toggle(inc, setInc, id)} />
            {inc.length > 0 && <p className="text-xs text-slate-400 mt-1">Empty = all countries</p>}
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1.5"><X className="h-3.5 w-3.5 text-red-400" /><span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Excluded Countries</span>{exc.length > 0 && <span className="text-xs text-slate-400">({exc.length})</span>}</div>
            <CountryList selected={exc} mode="exclude" countries={countries} onToggle={id => toggle(exc, setExc, id)} />
          </div>
        </div>

        {/* Column 3: Campaign Routing */}
        <div className="border border-slate-100 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0"><Megaphone className="h-4 w-4 text-purple-600" /></div>
            <div><p className="text-sm font-semibold text-purple-700">Campaign Routing</p><p className="text-xs text-slate-400">Campaign-based lead assignment</p></div>
          </div>
          {campaigns.length === 0 ? (
            <p className="text-xs text-slate-400">No campaigns yet. Campaign names appear here once prospects arrive from ad campaigns (e.g. Facebook Lead Ads).</p>
          ) : (
            <>
              <div>
                <div className="flex items-center gap-1.5 mb-1.5"><Check className="h-3.5 w-3.5 text-emerald-500" /><span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Included Campaigns</span>{incC.length > 0 && <span className="text-xs text-slate-400">({incC.length})</span>}</div>
                <CountryList selected={incC} mode="include" countries={campaigns.map(c => ({ id: c, name: c }))} onToggle={id => toggle(incC, setIncC, id)} />
                <p className="text-xs text-slate-400 mt-1">Empty = all campaigns</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1.5"><X className="h-3.5 w-3.5 text-red-400" /><span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Excluded Campaigns</span>{excC.length > 0 && <span className="text-xs text-slate-400">({excC.length})</span>}</div>
                <CountryList selected={excC} mode="exclude" countries={campaigns.map(c => ({ id: c, name: c }))} onToggle={id => toggle(excC, setExcC, id)} />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-4">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 h-10 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-sm font-semibold">
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save Assignment
        </button>
      </div>
    </div>
  );
}

function CountryList({ selected, mode, countries, onToggle }: { selected: string[]; mode: 'include' | 'exclude'; countries: Country[]; onToggle: (id: string) => void }) {
  const [q, setQ] = useState('');
  const filtered = q ? countries.filter(c => c.name.toLowerCase().includes(q.toLowerCase())) : countries;
  const chipBg = mode === 'include' ? '#E8F5E9' : '#FFEBEE';
  const chipText = mode === 'include' ? '#2E7D32' : '#C62828';
  const checkColor = mode === 'include' ? 'text-emerald-500' : 'text-red-400';
  const nameOf = (id: string) => countries.find(c => c.id === id)?.name ?? id;

  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selected.map(id => (
            <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: chipBg, color: chipText }}>
              {nameOf(id)}
              <button type="button" onClick={() => onToggle(id)} className="ml-0.5 leading-none hover:opacity-70" style={{ color: chipText }}>×</button>
            </span>
          ))}
        </div>
      )}
      <div className="border border-slate-100 rounded-lg overflow-hidden">
        <input className="w-full h-8 px-2.5 text-xs border-b border-slate-100 focus:outline-none placeholder:text-slate-300" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
        <div className="max-h-36 overflow-y-auto">
          {filtered.map(c => (
            <button key={c.id} type="button" onClick={() => onToggle(c.id)} className={`flex items-center justify-between gap-2 w-full px-2.5 py-1.5 text-xs text-left break-all hover:bg-slate-50 ${selected.includes(c.id) ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
              <span>{c.flagEmoji ? `${c.flagEmoji} ` : ''}{c.name}</span>
              {selected.includes(c.id) && <Check className={`h-3 w-3 flex-shrink-0 ${checkColor}`} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AddMemberModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [list, setList] = useState<{ id: string; name: string; email: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    saApi.get('/super-admin/demo/assignments/addable')
      .then(({ data }) => setList(Array.isArray(data) ? data : []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  const add = async (id: string) => {
    setBusy(id);
    try {
      await saApi.post(`/super-admin/demo/assignments/${id}`);
      toast('success', 'Added to demo team');
      onAdded();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to add'));
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Add to demo team</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
        ) : list.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">Everyone is already on the demo team.</p>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {list.map(a => (
              <div key={a.id} className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{a.name}</p>
                  <p className="text-xs text-slate-500 truncate">{a.email}</p>
                </div>
                <button onClick={() => add(a.id)} disabled={busy === a.id} className="px-3 h-8 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-xs font-semibold inline-flex items-center gap-1.5 flex-shrink-0">
                  {busy === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
